<template>
    <div
        id="CFormFileInput"
        class="input relative flex"
        dropzone
        @dragenter.prevent
        @dragleave.prevent="handleDragLeave"
        @dragover.prevent="handleDragOver"
        @drop.prevent="handleDrop"
    >
        <input
            ref="input"
            :accept="accept"
            class="hidden"
            :disabled="disabled"
            type="file"
            @change="handleFileChange"
        />

        <div v-if="isDropzoneActive" class="pointer-events-none absolute inset-0 bg-primary-500/50"></div>

        <div v-if="modelValue" class="flex max-w-full flex-auto items-center">
            <div class="truncate font-semibold">
                {{ modelValue.name }}
            </div>

            <div class="ml-2 flex-none text-sm italic text-opacity-25">({{ formatFileSize(modelValue.size) }})</div>

            <div class="-mr-1 ml-auto shrink-0 pl-2">
                <CButton v-if="withPreview" class="max-h-8 flex-none !px-2" variant="text" @click="preview()">
                    {{ $t('ui.CFormFileInput.show', lang) }}
                </CButton>

                <CButton class="!ml-0 max-h-8 flex-none !px-2" variant="text" @click="clear()">
                    {{ $t('ui.CFormFileInput.remove', lang) }}
                </CButton>
            </div>
        </div>

        <div v-else class="flex flex-auto cursor-pointer items-center" @click="openFilePicker()">
            <CButton class="-ml-3 max-h-8">
                {{ $t('ui.CFormFileInput.chooseFile', lang) }}
            </CButton>

            <div class="ml-auto text-center text-sm italic opacity-50">
                {{ $t('ui.CFormFileInput.suportedFormats', lang) }}:
                {{ supportedExtension }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

const props = defineProps({
    modelValue: {
        type: File,
        default: null
    },

    accept: {
        type: String,
        default: ''
    },

    pickLabel: {
        type: String,
        default: 'Vybrať súbor'
    },

    lang: {
        type: String,
        default: 'sk'
    },

    withPreview: Boolean,

    required: Boolean,

    disabled: Boolean
})

const emit = defineEmits(['update:modelValue'])

const input = ref<HTMLInputElement | null>(null)
const isDropzoneActive = ref(false)

const supportedExtension = computed(() => {
    return props.accept
        .replace(/\s/g, '')
        .split(',')
        .map((mime) => mime.split('/').pop()?.toUpperCase())
        .join(', ')
})

function formatFileSize(value: number): string {
    return `${(value / 1024 / 1024).toFixed(1)} MB`
}

function preview() {
    const file = props.modelValue as globalThis.File
    if (!file) return

    if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file)
        window.open(url, '_blank')
    } else {
        const link = document.createElement('a')
        link.href = URL.createObjectURL(file)
        link.download = file.name
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }
}

function openFilePicker() {
    input.value?.click()
}

function handleFileChange(e: Event) {
    const file = (e.target as HTMLInputElement).files?.[0]
    if (file) {
        emit('update:modelValue', file)
        if (input.value) {
            input.value.value = ''
        }
    }
}

function clear() {
    emit('update:modelValue', null)
}

function handleDragLeave() {
    isDropzoneActive.value = false
}

function handleDragOver() {
    isDropzoneActive.value = true
}

function handleDrop(e: DragEvent) {
    const file = e.dataTransfer?.files[0]
    if (file) {
        emit('update:modelValue', file)
    }
    isDropzoneActive.value = false
}
</script>
